import React, { useState, useEffect } from 'react';
import { ShieldCheck, Cookie, X, Check, SlidersHorizontal, Lock, ExternalLink } from 'lucide-react';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  status: 'accepted' | 'rejected' | 'custom';
  updatedAt: string;
}

const CONSENT_STORAGE_KEY = 'xweba_cookie_consent_v1';
const CONSENT_COOKIE_NAME = 'xweba_cookie_consent';

function setBrowserCookie(name: string, value: string, days = 365) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch {
    // Ignore cookie write errors in restricted sandboxes
  }
}

export const CookieConsentBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isEntered, setIsEntered] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(true);

  useEffect(() => {
    try {
      const savedRaw = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!savedRaw) {
        const timer = setTimeout(() => {
          setIsVisible(true);
          requestAnimationFrame(() => setIsEntered(true));
        }, 450);
        return () => clearTimeout(timer);
      } else {
        const parsed = JSON.parse(savedRaw) as CookiePreferences;
        setAnalyticsEnabled(Boolean(parsed.analytics));
        setMarketingEnabled(Boolean(parsed.marketing));
      }
    } catch {
      setIsVisible(true);
      setIsEntered(true);
    }
  }, []);

  useEffect(() => {
    const handleOpenCookieBanner = () => {
      setIsVisible(true);
      requestAnimationFrame(() => setIsEntered(true));
    };
    const handleOpenPrivacyPolicy = () => {
      setShowPrivacyModal(true);
    };

    window.addEventListener('xweba:open-cookie-banner', handleOpenCookieBanner);
    window.addEventListener('xweba:open-privacy-policy', handleOpenPrivacyPolicy);
    return () => {
      window.removeEventListener('xweba:open-cookie-banner', handleOpenCookieBanner);
      window.removeEventListener('xweba:open-privacy-policy', handleOpenPrivacyPolicy);
    };
  }, []);

  const persistConsent = (prefs: CookiePreferences) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(prefs));
    } catch {}
    setBrowserCookie(CONSENT_COOKIE_NAME, prefs.status, 365);
    setBrowserCookie(
      'xweba_cookie_prefs',
      `necessary:1,analytics:${prefs.analytics ? 1 : 0},marketing:${prefs.marketing ? 1 : 0}`,
      365
    );

    if (typeof window !== 'undefined' && (window as any).fbq) {
      try {
        (window as any).fbq('consent', prefs.marketing ? 'grant' : 'revoke');
      } catch {}
    }

    setIsEntered(false);
    setTimeout(() => {
      setIsVisible(false);
      setShowCustomize(false);
    }, 280);
  };

  const handleAcceptAll = () => {
    setAnalyticsEnabled(true);
    setMarketingEnabled(true);
    persistConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      status: 'accepted',
      updatedAt: new Date().toISOString(),
    });
  };

  const handleRejectAll = () => {
    setAnalyticsEnabled(false);
    setMarketingEnabled(false);
    persistConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      status: 'rejected',
      updatedAt: new Date().toISOString(),
    });
  };

  const handleSaveCustom = () => {
    persistConsent({
      necessary: true,
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
      status: 'custom',
      updatedAt: new Date().toISOString(),
    });
  };

  return (
    <>
      {/* Slide-Up GDPR Cookie Consent Banner */}
      {isVisible && (
        <aside
          role="region"
          aria-label="Cookie consent banner"
          className={`fixed bottom-0 left-0 right-0 z-40 p-3 sm:p-5 pointer-events-none transition-all duration-300 ease-out ${
            isEntered ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
          }`}
        >
          <div className="max-w-4xl mx-auto lg:mx-0 lg:ml-4 bg-[#0B2D52] text-white rounded-2xl border border-white/15 shadow-2xl p-4 sm:p-5 pointer-events-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3.5 flex-1">
                <div className="w-10 h-10 rounded-xl bg-[#F26522]/20 border border-[#F26522]/40 flex items-center justify-center text-[#F26522] shrink-0 mt-0.5">
                  <Cookie className="w-5 h-5" />
                </div>
                <div className="space-y-1 text-xs sm:text-sm text-slate-200 leading-relaxed">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-extrabold text-white text-sm">
                      We Value Your Privacy &amp; Data Sovereignty
                    </span>
                    <span className="px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[10px] font-mono font-bold uppercase">
                      GDPR Compliant
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    We use essential cookies to keep Xweba fast and secure, plus optional analytics and smart advertising cookies to improve your experience. Read our{' '}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-[#F26522] hover:text-orange-300 font-bold underline underline-offset-2 cursor-pointer"
                    >
                      Privacy &amp; Cookie Policy
                    </button>
                    .
                  </p>
                </div>
              </div>

              {/* Action Buttons: Accept, Reject, and Preferences */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowCustomize(!showCustomize)}
                  className="px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/15 text-slate-200 text-xs font-semibold transition-colors inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5 text-[#F26522]" />
                  <span>{showCustomize ? 'Hide Options' : 'Preferences'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-white/20 text-white text-xs font-bold transition-colors cursor-pointer"
                >
                  Reject
                </button>

                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 rounded-xl bg-[#F26522] hover:bg-[#d95314] text-white text-xs font-bold shadow-md transition-colors inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Accept</span>
                </button>
              </div>
            </div>

            {/* Granular Cookie Preferences Drawer */}
            {showCustomize && (
              <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10 flex flex-col justify-between">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-bold text-white flex items-center gap-1.5">
                        <Lock className="w-3.5 h-3.5 text-emerald-400" />
                        Strictly Necessary
                      </span>
                      <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase">
                        Always Active
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-300 leading-snug">
                      Required for core site navigation, session security, currency selection, and CSRF protection.
                    </p>
                  </div>

                  <label className="p-3 rounded-xl bg-white/5 border border-white/10 flex flex-col justify-between cursor-pointer hover:border-white/25 transition-colors">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-bold text-white">Analytics &amp; GEO</span>
                      <input
                        type="checkbox"
                        checked={analyticsEnabled}
                        onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                        className="w-4 h-4 accent-[#F26522] rounded cursor-pointer"
                      />
                    </div>
                    <p className="text-[11px] text-slate-300 leading-snug">
                      Helps us measure Core Web Vitals, Xwebaudit diagnostics, and anonymous page performance.
                    </p>
                  </label>

                  <label className="p-3 rounded-xl bg-white/5 border border-white/10 flex flex-col justify-between cursor-pointer hover:border-white/25 transition-colors">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-bold text-white">Smart Advertising</span>
                      <input
                        type="checkbox"
                        checked={marketingEnabled}
                        onChange={(e) => setMarketingEnabled(e.target.checked)}
                        className="w-4 h-4 accent-[#F26522] rounded cursor-pointer"
                      />
                    </div>
                    <p className="text-[11px] text-slate-300 leading-snug">
                      Supports attribution for our Digital / Smart Ads campaigns across Google, Meta, and LinkedIn.
                    </p>
                  </label>
                </div>

                <div className="flex items-center justify-between gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowPrivacyModal(true)}
                    className="text-xs text-slate-300 hover:text-white underline underline-offset-2 inline-flex items-center gap-1 cursor-pointer"
                  >
                    <span>View Full GDPR Privacy Policy</span>
                    <ExternalLink className="w-3 h-3 text-[#F26522]" />
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveCustom}
                    className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-colors cursor-pointer"
                  >
                    Save Selected Preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>
      )}

      {/* Privacy & Cookie Policy Modal */}
      {showPrivacyModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-policy-modal-title"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs"
        >
          <div className="bg-white text-slate-800 rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
            <div className="bg-[#0B2D52] text-white px-6 py-4 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="w-5 h-5 text-[#F26522]" />
                <div>
                  <h2 id="privacy-policy-modal-title" className="text-base font-extrabold text-white">
                    Xweba Privacy &amp; GDPR Cookie Policy
                  </h2>
                  <p className="text-[11px] text-slate-300">
                    Effective Date: January 2026 · EU GDPR &amp; Kenya Data Protection Act Compliant
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPrivacyModal(false)}
                aria-label="Close Privacy Policy"
                className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 text-xs sm:text-sm text-slate-600 leading-relaxed">
              <section className="space-y-1.5">
                <h3 className="text-sm font-bold text-[#0B2D52]">1. Data Controller &amp; Overview</h3>
                <p>
                  Xweba Digital Growth Partner (&ldquo;Xweba&rdquo;, <span className="font-mono text-xs">www.xweba.com</span>, <span className="font-mono text-xs">info@xweba.com</span>) respects your privacy and is committed to protecting personal data in compliance with the General Data Protection Regulation (EU GDPR) and the Data Protection Act.
                </p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-bold text-[#0B2D52]">2. How We Use Cookies</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <strong>Strictly Necessary Cookies:</strong> Essential for website security, session continuity, currency preference (USD/KES), and storing your cookie consent choice (<span className="font-mono text-xs">xweba_cookie_consent</span>).
                  </li>
                  <li>
                    <strong>Analytics &amp; Performance Cookies:</strong> Help us understand visitor interactions, Core Web Vitals speed, and Xwebaudit tool diagnostics.
                  </li>
                  <li>
                    <strong>Smart Advertising Cookies:</strong> Used only with your consent to measure campaign effectiveness across Google, Meta, and LinkedIn.
                  </li>
                </ul>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-bold text-[#0B2D52]">3. CRM, Audit &amp; AI Concierge Data</h3>
                <p>
                  When you voluntarily submit your name, email address, or phone number via our Quote Request, Contact Form, Xwebaudit scanner, or AI Growth Concierge chat, we process those details solely to deliver your requested audit report, proposal, or consultation via <span className="font-mono text-xs">info@xweba.com</span> and WhatsApp (<span className="font-mono text-xs">+254 725 724 175</span>). We never sell your personal data to third parties.
                </p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-bold text-[#0B2D52]">4. Your Rights Under GDPR</h3>
                <p>
                  You have the right to access, rectify, erase, or restrict processing of your personal data, and to withdraw cookie consent at any time. To exercise your rights, contact our Data Protection Desk at{' '}
                  <a href="mailto:info@xweba.com" className="text-[#F26522] font-bold hover:underline">
                    info@xweba.com
                  </a>
                  .
                </p>
              </section>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPrivacyModal(false);
                    handleRejectAll();
                  }}
                  className="px-3.5 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold transition-colors cursor-pointer"
                >
                  Reject Non-Essential
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPrivacyModal(false);
                    handleAcceptAll();
                  }}
                  className="px-4 py-2 rounded-xl bg-[#F26522] hover:bg-[#d95314] text-white text-xs font-bold transition-colors cursor-pointer"
                >
                  Accept All Cookies
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowPrivacyModal(false)}
                className="px-4 py-2 rounded-xl bg-[#0B2D52] hover:bg-[#133d6b] text-white text-xs font-bold transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
